import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageEditorComponent } from '@syncfusion/ej2-react-image-editor';
import { getComponent } from '@syncfusion/ej2-base';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';

type ToolbarClickArgs = { item: { id?: string; [k: string]: unknown } };

interface Props {
  /** Authenticated proxy URL (/api/v1/chat/messages/attachment/?key=...) */
  imageUrl: string;
  imageName: string;
  /** Username of the other participant — needed to re-upload the edited image */
  contactUsername: string;
  onSave: (newUrl: string, newName: string) => void;
  onClose: () => void;
}

const TOOLBAR_ITEMS = [
  'Annotate', 'Crop', 'Transform', 'Filter', 'Finetune',
  'ZoomIn', 'ZoomOut', 'Undo', 'Redo', 'Reset', 'Frame', 'Resize',
  {
    text: '', tooltipText: 'Save edited image',
    prefixIcon: 'e-icons e-save', id: 'chat-save',
  },
];

export default function ChatImageEditor({ imageUrl, imageName, contactUsername, onSave, onClose }: Props) {
  const { token } = useAuth();
  const editorRef = useRef<ImageEditorComponent>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCreated = async () => {
    if (!editorRef.current || !token) return;
    try {
      // Fetch the authenticated image as a blob then load into editor
      const endpoint = imageUrl.replace(/^\/api\/v1/, '');
      const r = await apiCall(endpoint, { method: 'GET' }, token);
      if (!r.ok) throw new Error('Failed to fetch image');
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      editorRef.current.open(blobUrl);
    } catch (e) {
      setError('Could not load image for editing');
    }
  };

  const onScroll = () => {
    const sliderEl = document.getElementById('chat-image-editor_sliderWrapper');
    if (sliderEl) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const slider = getComponent(sliderEl, 'slider') as any;
      slider?.refreshTooltip(slider.tooltipTarget);
    }
  };

  const doSave = async () => {
    if (saving || !editorRef.current || !token) return;
    setError(null);
    setSaving(true);
    try {
      const imageData = editorRef.current.getImageData();
      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      canvas.getContext('2d')!.putImageData(imageData, 0, 0);

      const blob = await new Promise<Blob>((res, rej) =>
        canvas.toBlob(b => b ? res(b) : rej(new Error('Canvas export failed')), 'image/png')
      );

      const ext = imageName.includes('.') ? imageName : imageName + '.png';
      const editedName = ext.replace(/\.[^.]+$/, '_edited.png');
      const file = new File([blob], editedName, { type: 'image/png' });

      const form = new FormData();
      form.append('file', file);
      form.append('to', contactUsername);

      const r = await apiCall(ENDPOINTS.CHAT.UPLOAD, { method: 'POST', body: form }, token);
      if (!r.ok) throw new Error(`Upload failed: ${r.status}`);
      const data = await r.json();

      onSave(data.url, editedName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setSaving(false);
    }
  };

  const onToolbarItemClicked = (args: ToolbarClickArgs) => {
    if (args.item.id === 'chat-save') void doSave();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80">
      <div className="relative flex flex-col bg-base-100 rounded-box shadow-2xl w-[92vw] max-w-6xl h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-base-300 shrink-0">
          <h2 className="font-semibold text-lg">Edit Image</h2>
          <div className="flex items-center gap-3">
            {saving && (
              <>
                <span className="loading loading-spinner loading-sm" />
                <span className="text-sm text-base-content/60">Saving…</span>
              </>
            )}
            {error && <span className="text-sm text-error">{error}</span>}
            <button className="btn btn-sm btn-ghost btn-circle" onClick={onClose} disabled={saving}>✕</button>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-hidden" onScroll={onScroll}>
          <ImageEditorComponent
            id="chat-image-editor"
            ref={editorRef}
            created={onCreated}
            toolbarItemClicked={onToolbarItemClicked}
            toolbar={TOOLBAR_ITEMS}
            height="100%"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
